import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

/**
 * Checks if a given URL string matches known API patterns.
 * @param urlString - The URL string to check.
 * @returns True if the URL matches an API pattern, false otherwise.
 */
const isApiRequestUrl = (urlString: string): boolean => {
    try {
        const url = new URL(urlString);
        return url.pathname.startsWith('/api/') ||
            url.href.includes('getUserDetails') ||
            url.pathname.includes('/users/');
    } catch (err) {
        console.error('Error parsing URL during API check:', urlString, err);
        return false; // Treat URLs that fail to parse as non-API requests
    }
};

/**
 * Attempts to delete API requests from a single cache.
 * @param cacheName - The name of the cache to process.
 */
const processCacheForApiRequests = async (cacheName: string): Promise<void> => {
    try {
        const cache = await caches.open(cacheName);
        if (!cache) return;

        const requests = await cache.keys();
        if (!requests || requests.length === 0) return;

        const deletePromises = requests.map(async (request) => {
            if (isApiRequestUrl(request.url)) {
                try {
                    await cache.delete(request);
                    console.log(`Deleted cached API request for ${request.url}`);
                } catch (deleteErr) {
                    console.error(`Error deleting cached request for ${request.url}:`, deleteErr);
                }
            }
        });

        await Promise.all(deletePromises);
    } catch (openOrKeysErr) {
        console.error(`Error processing cache "${cacheName}":`, openOrKeysErr);
    }
};

/**
 * Clears API-related entries from browser caches.
 */
const clearBrowserApiCache = async (): Promise<void> => {
    if (!('caches' in window)) return;

    try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(processCacheForApiRequests));
    } catch (error) {
        console.error('Error clearing browser API cache:', error);
    }
};

/**
 * This component handles clearing the service worker and browser cache for API requests
 * especially when authentication state changes or when navigating to/from the login page.
 */
export default function CacheCleaner() {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // Clear cache when authentication state changes
    useEffect(() => {
        if (isLoading) return; // Don't run on initial load/during authentication check

        const clearCaches = async () => {
            // 1. Clear via Service Worker
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                console.log('Requesting service worker to clear API cache');
                navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
            }
            // 2. Clear Browser Cache (best effort)
            await clearBrowserApiCache();
        };

        clearCaches();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]); // Only re-run when authentication state changes

    // Also clear on login page navigation
    useEffect(() => {
        const isLoginPage = location.pathname.includes('/login');
        if (isLoginPage) {
            const clearCachesOnLogin = async () => {
                if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                    console.log('Requesting service worker to clear API cache on login page navigation');
                    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
                }
                // Optionally clear browser cache here too if needed on login navigation
                // await clearBrowserApiCache(); 
            };
            clearCachesOnLogin();
        }
    }, [location.pathname]);

    // This component doesn't render anything
    return null;
} 